import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useInView } from 'react-intersection-observer';
import { useIntl } from 'react-intl';
import { HERO_CONFIG } from '../config/constants';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useVideoManager } from '../hooks/useVideoManager';
import { useVideoLoader } from '../hooks/useVideoLoader';
import { useTypingAnimation } from '../hooks/useTypingAnimation';
import VideoManager from '../components/VideoManager';
import FallbackImageSlider from '../components/FallbackImageSlider';
import LoadingIndicator from '../components/LoadingIndicator';
import HeroContent from '../components/HeroContent';

const { VIDEOS, VIDEO_DURATIONS, FALLBACK_IMAGES, FALLBACK_IMAGE_DURATION } =
  HERO_CONFIG;

export default function Hero() {
  const intl = useIntl();
  const { isSlowConnection } = useNetworkStatus();

  // State管理
  const [heroState, setHeroState] = useState({
    currentIndex: 0,
    isDesktop: false,
    videoDurations: VIDEOS.map(() => 0),
    videosLoaded: false,
    firstVideoReady: false,
    loadingVideos: false,
    videoErrors: VIDEOS.map(() => false),
    fallbackImageIndex: 0,
  });

  // Refs
  const videoRefs = useRef(VIDEOS.map(() => React.createRef()));
  const timeoutRefs = useRef({ main: null });
  const transitionCallbackRef = useRef(null);

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // カスタムフック
  const updateHeroState = useCallback(updates => {
    setHeroState(prev => ({ ...prev, ...updates }));
  }, []);

  const { createHandleLoadedMetadata, playVideo, pauseVideo } = useVideoManager(
    videoRefs,
    updateHeroState
  );

  // 動画読み込みフック（AbortController対応）
  useVideoLoader(
    inView,
    heroState,
    updateHeroState,
    videoRefs,
    isSlowConnection
  );

  // ヒーロータイトル取得関数
  const getHeroTitle = useCallback(() => {
    const locale = intl.locale;
    if (locale === 'ja' && heroState.isDesktop) {
      return intl.formatMessage({ id: 'hero.title.desktop' });
    }
    return intl.formatMessage({ id: 'hero.title' });
  }, [intl, heroState.isDesktop]);

  // Typing animation hook
  const typingState = useTypingAnimation(inView, getHeroTitle());

  // デバウンスされたresize処理
  const debouncedResize = useMemo(() => {
    let timeoutId;
    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        updateHeroState({
          isDesktop: window.innerWidth >= 1024, // lg以上で改行あり
        });
      }, 100);
    };
  }, [updateHeroState]);

  // resize イベントリスナー
  useEffect(() => {
    const checkScreenSize = () => {
      updateHeroState({
        isDesktop: window.innerWidth >= 1024, // lg以上で改行あり
      });
    };

    checkScreenSize();
    window.addEventListener('resize', debouncedResize);
    return () => {
      window.removeEventListener('resize', debouncedResize);
    };
  }, [debouncedResize, updateHeroState]);

  // 動画切り替え管理
  const startTransition = useCallback(() => {
    if (!heroState.firstVideoReady) return;

    const newNextIndex = (heroState.currentIndex + 1) % VIDEOS.length;

    if (process.env.NODE_ENV === 'development') {
      console.log(
        `Starting transition: ${heroState.currentIndex} -> ${newNextIndex}`
      );
    }

    // 次の動画の準備
    const nextVideo = videoRefs.current[newNextIndex].current;
    if (nextVideo) {
      nextVideo.currentTime = 0;

      if (process.env.NODE_ENV === 'development') {
        console.log(`Next video ${newNextIndex + 1} prepared`);
      }
    }

    // 即座に切り替え
    updateHeroState({ currentIndex: newNextIndex });

    if (process.env.NODE_ENV === 'development') {
      console.log(`Transition completed: currentIndex = ${newNextIndex}`);
    }

    // 次のトランジションをスケジュール
    if (transitionCallbackRef.current) {
      transitionCallbackRef.current(newNextIndex);
    }
  }, [heroState.currentIndex, heroState.firstVideoReady, updateHeroState]);

  const scheduleNextTransition = useCallback(
    index => {
      if (timeoutRefs.current.main) {
        clearTimeout(timeoutRefs.current.main);
      }

      const actualDuration =
        heroState.videoDurations[index] || VIDEO_DURATIONS[index] * 1000;
      const minDisplayTime = index === 1 ? 2000 : 3000;
      const timeUntilTransition = Math.max(
        actualDuration - 1000,
        minDisplayTime
      );

      if (process.env.NODE_ENV === 'development') {
        console.log(
          `Video ${index + 1} timing: actual=${actualDuration}ms, transition=${timeUntilTransition}ms`
        );
      }

      timeoutRefs.current.main = setTimeout(() => {
        if (process.env.NODE_ENV === 'development') {
          console.log(
            `Timer-based transition triggered for video ${index + 1}`
          );
        }
        startTransition();
      }, timeUntilTransition);

      if (process.env.NODE_ENV === 'development') {
        console.log(
          `Scheduled transition for video ${index + 1} in ${timeUntilTransition}ms`
        );
      }
    },
    [startTransition, heroState.videoDurations]
  );

  // メモリリーク対策：refに直接関数を代入せず、useEffectで管理
  useEffect(() => {
    transitionCallbackRef.current = scheduleNextTransition;
  }, [scheduleNextTransition]);

  // 動画の再生制御
  useEffect(() => {
    const cleanupFunctions = [];
    // timeoutRefs.currentをローカル変数にコピー
    const currentTimeoutRefs = timeoutRefs.current;

    // 全ての動画を一旦停止
    videoRefs.current.forEach((ref, index) => {
      const video = ref.current;
      if (video) {
        pauseVideo(index);
      }
    });

    // 少し遅延してから現在の動画を再生
    const playTimeout = setTimeout(() => {
      videoRefs.current.forEach((ref, index) => {
        const video = ref.current;
        if (video) {
          const handleLoadedMetadata = createHandleLoadedMetadata(index);
          video.addEventListener('loadedmetadata', handleLoadedMetadata);
          cleanupFunctions.push(() => {
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
          });

          // 現在の動画のみ再生
          if (index === heroState.currentIndex && heroState.firstVideoReady) {
            playVideo(index);
          }
        }
      });

      // 動画が準備完了してからスケジュールを開始
      if (heroState.firstVideoReady) {
        scheduleNextTransition(heroState.currentIndex);
      }
    }, 100);

    return () => {
      clearTimeout(playTimeout);
      if (currentTimeoutRefs && currentTimeoutRefs.main)
        clearTimeout(currentTimeoutRefs.main);
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [
    heroState.currentIndex,
    heroState.firstVideoReady,
    scheduleNextTransition,
    createHandleLoadedMetadata,
    playVideo,
    pauseVideo,
  ]);

  // フォールバック画像のスライドショー
  useEffect(() => {
    // 全ての動画でエラーが発生している場合、または低速ネットワークの場合のみフォールバック画像をスライドショー
    const shouldShowFallback =
      heroState.videoErrors.every(error => error) || isSlowConnection;

    if (!shouldShowFallback) {
      return;
    }

    const interval = setInterval(() => {
      setHeroState(prev => {
        const nextIndex =
          (prev.fallbackImageIndex + 1) % FALLBACK_IMAGES.length;
        if (process.env.NODE_ENV === 'development') {
          console.log('Fallback image switched to:', nextIndex);
        }
        return {
          ...prev,
          fallbackImageIndex: nextIndex,
        };
      });
    }, FALLBACK_IMAGE_DURATION);

    return () => clearInterval(interval);
  }, [heroState.videoErrors, isSlowConnection]);

  // フォールバック表示の判定
  const shouldShowFallback =
    heroState.videosLoaded &&
    (heroState.videoErrors.every(error => error) || isSlowConnection);

  const heroContentState = {
    ...heroState,
    ...typingState,
  };

  return (
    <section
      ref={ref}
      id="hero-section"
      data-testid="hero-section"
      className="relative h-screen overflow-hidden bg-gray-900"
    >
      <VideoManager
        videoRefs={videoRefs}
        heroState={heroState}
        updateHeroState={updateHeroState}
        startTransition={startTransition}
        createHandleLoadedMetadata={createHandleLoadedMetadata}
      />

      <div className="absolute inset-0 z-30 bg-gradient-to-b from-black/40 via-black/20 to-black/50" />

      <LoadingIndicator
        show={heroState.loadingVideos && !heroState.videosLoaded}
      />

      <FallbackImageSlider
        heroState={heroState}
        isSlowConnection={isSlowConnection}
        show={shouldShowFallback}
      />

      <HeroContent heroState={heroContentState} getHeroTitle={getHeroTitle} />
    </section>
  );
}
